import React, { useState, useCallback, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Loader } from 'components/loader/Loader';
import './Dropdown.scss';

interface IDropdown {
	isOpen?: boolean;
	disabled?: boolean;
	dropdownHeader?: JSX.Element;
	listTitle?: string | JSX.Element;
	options?: object;
	position?: string;
	closeByTimeout?: boolean;
	listOverflow?: boolean;
	bodyClass?: string;
	headerClass?: string;
	wrapperClass?: string;
	isLoading?: boolean;
	title?: string | JSX.Element;
}

const getPositionClass = (position?: string) => {
	switch (position) {
		case 'bottom-left':
			return 'dropdown__body--position--bottom-left';
		case 'bottom-center':
			return 'dropdown__body--position--bottom-center';
		case 'bottom-right':
			return 'dropdown__body--position--bottom-right';
		case 'top-left':
			return 'dropdown__body--position--top-left';
		case 'top-center':
			return 'dropdown__body--position--top-center';
		case 'top-right':
			return 'dropdown__body--position--top-right';
		default:
			return '';
	}
};

export const Dropdown: React.FunctionComponent<IDropdown> = (props) => {
	const {
		isOpen: isOpenInitial,
		children,
		dropdownHeader,
		listTitle,
		position,
		listOverflow,
		bodyClass,
		headerClass,
		wrapperClass,
		disabled,
		closeByTimeout,
		isLoading,
		title,
	} = props;
	const positionClass = getPositionClass(position);
	const [isOpen, setIsOpen] = useState(isOpenInitial || false);
	const toggleDropdown = useCallback(() => !isLoading && setIsOpen(!isOpen), [
		isOpen,
		isLoading,
	]);
	const wrapperRef = useRef<HTMLDivElement>(null);
	let timerId: NodeJS.Timeout;

	useEffect(() => {
		if (!isOpen) return;

		const outerClickHandler = (e: MouseEvent) => {
			const target = e.target as Node;
			const { current: wrapper } = wrapperRef;

			if (!wrapper || wrapper.contains(target) || disabled) return;

			toggleDropdown();
		};

		window.addEventListener('click', outerClickHandler);

		return () => window.removeEventListener('click', outerClickHandler);
	}, [isOpen, disabled, toggleDropdown]);

	const handleMouseOver = () => {
		clearTimeout(timerId);
	};

	const handleMouseLeave = () => {
		timerId = setTimeout(() => {
			setIsOpen(false);
		}, 500);
	};

	const handleFocus = () => {};

	const timeoutProps = closeByTimeout
		? {
				onFocus: handleFocus,
				onMouseOver: handleMouseOver,
				onMouseLeave: handleMouseLeave,
		  }
		: {};

	return (
		<div
			ref={wrapperRef}
			{...timeoutProps}
			className={classNames('dropdown__wrapper', wrapperClass)}
		>
			<div
				className={classNames('dropdown__header', headerClass)}
				onClick={toggleDropdown}
			>
				{title}
				{dropdownHeader || (
					<div
						className={classNames('dropdown__tick', {
							'dropdown__tick--open': isOpen,
						})}
					/>
				)}
			</div>
			{isOpen && (
				<div
					className={classNames(
						'dropdown__body',
						'relative',
						bodyClass,
						positionClass,
						{
							'dropdown__body--overflow': listOverflow,
						},
					)}
				>
					{listTitle && <div className="dropdown__title">{listTitle}</div>}
					{children}
					{!closeByTimeout && (
						<div
							className={classNames('dropdown__close', {
								disabled: isLoading,
							})}
							onClick={toggleDropdown}
						/>
					)}
					<Loader
						wrapperClass="loader__overlay--transparent"
						isLoading={isLoading}
						overlay
					/>
				</div>
			)}
		</div>
	);
};
